import { WritableDraft, produce } from 'immer';
import { create } from 'zustand';

import { AnyBlock } from '../types';

type PageBuilderStore = {
  blocks: AnyBlock[];
  editingBlockId: string | null;
  setEditingBlockId: (blockId: string | null) => void;
  setBlocks: (
    blocks:
      | PageBuilderStore['blocks']
      | ((blocks: PageBuilderStore['blocks']) => PageBuilderStore['blocks']),
  ) => void;
  addBlock: (block: AnyBlock) => void;
  removeBlock: (blockId: AnyBlock['id']) => void;
  updateBlock: (blockId: AnyBlock['id'], newBlock: AnyBlock) => void;
  resetBlocks: () => void;
};

export const usePageBuilderStore = create<PageBuilderStore>((set) => ({
  blocks: [],
  editingBlockId: null,

  setEditingBlockId: (blockId) => set({ editingBlockId: blockId }),
  setBlocks: (blocks) => {
    // Support when blocks is a callback
    if (typeof blocks === 'function') {
      return set((state) => ({
        blocks: blocks(state.blocks),
      }));
    }
    return set({ blocks });
  },
  addBlock: (block) =>
    set(({ blocks }) => ({
      blocks: produce(blocks, (draft) => {
        draft.push(block as WritableDraft<AnyBlock>);
      }),
    })),
  removeBlock: (blockId) =>
    set(({ blocks, editingBlockId }) => ({
      blocks: produce(blocks, (draft) => {
        return draft.filter((block) => block.id !== blockId);
      }),
      editingBlockId: editingBlockId === blockId ? null : editingBlockId,
    })),
  updateBlock: (blockId, newBlock) =>
    set(({ blocks }) => ({
      blocks: produce(blocks, (draft) => {
        const index = draft.findIndex((block) => block.id === blockId);
        if (index !== -1) draft[index] = newBlock as WritableDraft<AnyBlock>;
      }),
    })),
  resetBlocks: () => set({ blocks: [], editingBlockId: null }),
}));
